import { usePollingQuery, type PollDecision } from "@hooks/usePollingQuery";
import { API_URL, fetchGraphQL } from "@relay";
import { useState } from "react";
import { graphql } from "react-relay";
import type { DoorUnlockPollingQuery, DoorUnlockPollingQuery$data, HubStatus } from "./__generated__/DoorUnlockPollingQuery.graphql";
import { initialDoorUnlockState, transitionDoorUnlock, type DoorUnlockPhase } from "./door-unlock-machine";

export type DoorUnlockOutcome =
	| { type: "opened" }
	| { type: "closed" }
	| { type: "timed_out"; message?: string }
	| { type: "error"; message?: string };

export type DoorUnlockTarget = "opened" | "closed";

const query = graphql`
	query DoorUnlockPollingQuery($unlockRequestId: UUID!) {
		unlockRequest(id: $unlockRequestId) {
			id
			status
			error
			unlockedAt
			reservation {
				space {
					id
					status
					hub {
						status
					}
				}
			}
		}
	}
`;

const rawPollingQuery = `
	query DoorUnlockRawPollingQuery($accessToken: String!) {
		anonymousUtilizationGrant(accessToken: $accessToken) {
			reservation {
				space {
					id
					status
					hub { status }
				}
				unlockRequests(
					orderBy: [{ field: CREATED_AT, ordering: DESC }]
					pagination: { page: 0, pageSize: 100 }
				) {
					nodes {
						id
						status
						error
						unlockedAt
					}
				}
			}
		}
	}
`;

type UseDoorUnlockPollingProps = {
	unlockRequestId: string;
	accessToken?: string;
	target: DoorUnlockTarget;
	onTerminate: (outcome: DoorUnlockOutcome) => void;
	retryKey?: number;
};

export function useDoorUnlockPolling(props: UseDoorUnlockPollingProps) {
	const { unlockRequestId, accessToken, target, onTerminate, retryKey = 0 } = props;
	// Preserve proof of OPEN across refreshes in this browser tab. Without it,
	// TIMED_OUT/ERROR + CLOSED cannot be distinguished from a door that never opened.
	const evidenceKey = `door-unlock-open:${unlockRequestId}`;
	let storedOpeningEvidence = false;
	try {
		storedOpeningEvidence = sessionStorage.getItem(evidenceKey) === "true";
	} catch {
		// The backend UNLOCKED state remains the durable fallback when storage is unavailable.
	}
	const initialState = storedOpeningEvidence ? { phase: "waiting_for_close" as const, hasObservedOpen: true } : initialDoorUnlockState;
	const [phaseState, setPhaseState] = useState<{ phase: DoorUnlockPhase; retryKey: number }>({ phase: initialState.phase, retryKey });
	const phase = phaseState.retryKey === retryKey ? phaseState.phase : initialState.phase;
	const [hubStatus, setHubStatus] = useState<HubStatus>("ONLINE");
	const polling = usePollingQuery<DoorUnlockPollingQuery, DoorUnlockOutcome>({
		query,
		variables: { unlockRequestId },
		fetcher: accessToken
			? async () => {
					const data = await fetchGraphQL<{
						anonymousUtilizationGrant: {
							reservation: {
								space: NonNullable<NonNullable<DoorUnlockPollingQuery$data["unlockRequest"]>["reservation"]>["space"];
								unlockRequests: {
									nodes: Omit<NonNullable<DoorUnlockPollingQuery$data["unlockRequest"]>, "reservation">[];
								};
							};
						};
					}>(rawPollingQuery, { accessToken });
					const reservation = data.anonymousUtilizationGrant.reservation;
					const unlockRequest = reservation.unlockRequests.nodes.find((request) => request.id === unlockRequestId);
					return {
						unlockRequest: unlockRequest ? { ...unlockRequest, reservation: { space: reservation.space } } : null,
					};
				}
			: undefined,
		createEvaluator: () => {
			// The evaluator lives for one polling session and keeps the previous
			// snapshot in memory, so OPEN followed by CLOSED can complete the cycle.
			let machineState = initialState;
			return (data: DoorUnlockPollingQuery$data): PollDecision<DoorUnlockOutcome> => {
				// A newly-created request can be temporarily absent because of backend
				// propagation. Keep polling instead of reporting a false error.
				if (!data.unlockRequest) return { type: "continue" };
				const polledSpace = data.unlockRequest.reservation?.space;
				if (!polledSpace) return { type: "continue" };
				const nextState = transitionDoorUnlock(machineState, {
					doorStatus: polledSpace.status,
					unlockStatus: data.unlockRequest.status,
					unlockedAt: data.unlockRequest.unlockedAt,
					error: data.unlockRequest.error,
				});
				if (nextState.phase !== machineState.phase) setPhaseState({ phase: nextState.phase, retryKey });
				machineState = nextState;
				try {
					if (nextState.hasObservedOpen) sessionStorage.setItem(evidenceKey, "true");
					if (nextState.phase === "closed") sessionStorage.removeItem(evidenceKey);
				} catch {
					// The backend UNLOCKED state remains the durable fallback when storage is unavailable.
				}
				if (
					polledSpace.hub.status === "OFFLINE" &&
					(nextState.phase === "timed_out" || nextState.phase === "error")
				) {
					return { type: "continue" };
				}
				let outcome: DoorUnlockOutcome | undefined;
				if (nextState.phase === "closed") outcome = { type: "closed" };
				else if (nextState.phase === "timed_out" || nextState.phase === "error") {
					outcome = { type: nextState.phase, message: nextState.message };
				} else if (nextState.phase === "waiting_for_close" && target === "opened") {
					// Opening screens stop at OPEN; closing screens keep polling until CLOSED.
					outcome = { type: "opened" };
				}
				return outcome
					? { type: "complete", result: outcome }
					: { type: "continue", cancelDeadline: nextState.phase === "waiting_for_close" };
			};
		},
		onComplete: onTerminate,
		onData: (data) => {
			const status = data.unlockRequest?.reservation?.space.hub.status;
			if (status) setHubStatus(status);
		},
		intervalMs: target === "opened" ? 100 : 1000,
		minTimeMs: target === "opened" ? 0 : 500,
		delayedMs: 10_000,
		enabled: Boolean(API_URL),
		sessionKey: `${unlockRequestId}:${target}:${retryKey}`,
	});

	return { phase, hubStatus, ...polling };
}
